import { create } from "zustand";
import { GetProjectFilesListResponseSchema } from "../../darvin-shared/schema/zod/operations/get-project-files-list-response";
import { ProjectFilesApi } from "../api/projectFilesApi";

export enum ProjectFilesListStatus {
  INITIAL = "Initial",
  LOADING_FILES_LIST = "LoadingFilesList",
  LOADING_FILE = "LoadingFile",
  LOADED = "Loaded",
}

interface ProjectFilesListActions {
  loadForProject: (projectId: string) => Promise<void>;
  onFileSelected: (filePath: string | null) => Promise<void>;
  reset: () => void;
}

interface ProjectFilesListState {
  status: ProjectFilesListStatus;
  projectId: string;
  files: string[];
  revisionId: string;
  selectedFilePath: string | null;
  selectedFileContent: string | null;

  actions: ProjectFilesListActions;
}

export const useProjectFilesListStore = create<ProjectFilesListState>((set, get) => {
  return {
    status: ProjectFilesListStatus.INITIAL,
    projectId: "",
    files: [],
    revisionId: "",
    selectedFilePath: null,
    selectedFileContent: null,

    actions: {
      loadForProject: async (projectId: string) => {
        set({
          status: ProjectFilesListStatus.LOADING_FILES_LIST,
        });

        const rawResponse = await ProjectFilesApi.loadProjectFilesList(
          projectId
        );
        const parsedResponse =
          GetProjectFilesListResponseSchema.parse(rawResponse);

        set({
          status: ProjectFilesListStatus.LOADED,
          projectId,
          files: parsedResponse.files,
          revisionId: parsedResponse.revisionId,
        });
      },

      onFileSelected: async (filePath: string | null) => {
        if (!filePath) {
          set({
            status: ProjectFilesListStatus.LOADED,
            selectedFilePath: null,
            selectedFileContent: null,
          });
          return;
        }

        set({
          status: ProjectFilesListStatus.LOADING_FILE,
          selectedFilePath: filePath,
          selectedFileContent: null,
        });

        const fileContent = await ProjectFilesApi.loadProjectFile(
          get().projectId,
          get().revisionId,
          filePath
        );

        set({
          status: ProjectFilesListStatus.LOADED,
          selectedFileContent: fileContent,
        });
      },

      reset: () => {
        set({
          status: ProjectFilesListStatus.INITIAL,
          projectId: "",
          files: [],
          revisionId: "",
          selectedFilePath: null,
          selectedFileContent: null,
        });
      },
    },
  };
});

export const useProjectFilesListStatus = () =>
  useProjectFilesListStore((state) => state.status);

export const useProjectFilesListFiles = () =>
  useProjectFilesListStore((state) => state.files);

export const useProjectFilesSelectedFilePath = () =>
  useProjectFilesListStore((state) => state.selectedFilePath);

export const useProjectFilesSelectedFileContent = () =>
  useProjectFilesListStore((state) => state.selectedFileContent);

export const useProjectFilesListActions = () =>
  useProjectFilesListStore((state) => state.actions);
